import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Stack,
  Pagination,
  Card,
  CardContent,
  Button,
  Grid,
  CardMedia
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  TrendingUp,
  NewReleases,
  PlayArrow
} from '@mui/icons-material';
import CampaignCard from '../components/CampaignCard';
import { 
  dummyCampaigns, 
  dummyCategories,
  getPopularCampaigns,
  getNewCampaigns,
  getOngoingCampaigns
} from '../utils/dummyData';

// 이미지 URL을 https://placehold.co/로 변경
const getPlaceholderImage = (text: string) => {
  return `https://placehold.co/400x300/e0e0e0/666666?text=${encodeURIComponent(text)}`;
};

const CampaignListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const campaignsPerPage = 9;

  // 필터링된 캠페인 목록
  const filteredCampaigns = dummyCampaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
                           dummyCategories.find(cat => cat.id === selectedCategory)?.type === campaign.category;
    
    const matchesType = selectedType === 'all' || campaign.type === selectedType;
    
    return matchesSearch && matchesCategory && matchesType;
  });

  // 정렬된 캠페인 목록
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.participantCount - a.participantCount;
      case 'reward':
        return b.reward.points - a.reward.points;
      case 'deadline':
        return a.deadline.getTime() - b.deadline.getTime();
      case 'latest':
      default:
        return b.createdAt.getTime() - a.createdAt.getTime();
    }
  });

  // 페이지네이션
  const totalPages = Math.ceil(sortedCampaigns.length / campaignsPerPage);
  const startIndex = (currentPage - 1) * campaignsPerPage;
  const currentCampaigns = sortedCampaigns.slice(startIndex, startIndex + campaignsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* 헤더 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          캠페인 목록
        </Typography>
        <Typography variant="body1" color="text.secondary">
          다양한 상품과 플레이스를 체험하고 리뷰를 작성해보세요!
        </Typography>
      </Box>

      {/* 필터 및 검색 */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2,
            alignItems: 'center'
          }}>
            <Box sx={{ flex: 2, minWidth: 200 }}>
              <TextField
                fullWidth
                placeholder="캠페인 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel>카테고리</InputLabel>
                <Select
                  value={selectedCategory}
                  label="카테고리"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <MenuItem value="all">전체</MenuItem>
                  {dummyCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel>타입</InputLabel>
                <Select
                  value={selectedType}
                  label="타입"
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <MenuItem value="all">전체</MenuItem>
                  <MenuItem value="popular">인기</MenuItem>
                  <MenuItem value="new">신규</MenuItem>
                  <MenuItem value="ongoing">진행중</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <FormControl fullWidth>
                <InputLabel>정렬</InputLabel>
                <Select
                  value={sortBy}
                  label="정렬"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="latest">최신순</MenuItem>
                  <MenuItem value="popular">참여자순</MenuItem>
                  <MenuItem value="reward">리워드순</MenuItem>
                  <MenuItem value="deadline">마감순</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                  setSelectedType('all');
                  setSortBy('latest');
                  setCurrentPage(1);
                }}
              >
                초기화
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 빠른 필터 칩 */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            icon={<TrendingUp />}
            label={`인기 캠페인 (${getPopularCampaigns().length})`}
            onClick={() => setSelectedType('popular')}
            variant={selectedType === 'popular' ? 'filled' : 'outlined'}
            color={selectedType === 'popular' ? 'primary' : 'default'}
          />
          <Chip
            icon={<NewReleases />}
            label={`신규 캠페인 (${getNewCampaigns().length})`}
            onClick={() => setSelectedType('new')}
            variant={selectedType === 'new' ? 'filled' : 'outlined'}
            color={selectedType === 'new' ? 'primary' : 'default'}
          />
          <Chip
            icon={<PlayArrow />}
            label={`진행중 캠페인 (${getOngoingCampaigns().length})`}
            onClick={() => setSelectedType('ongoing')}
            variant={selectedType === 'ongoing' ? 'filled' : 'outlined'}
            color={selectedType === 'ongoing' ? 'primary' : 'default'}
          />
        </Stack>
      </Box>

      {/* 결과 요약 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          총 {sortedCampaigns.length}개의 캠페인을 찾았습니다.
        </Typography>
      </Box>

      {/* 캠페인 목록 */}
      {currentCampaigns.length > 0 ? (
        <>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: 3,
            mb: 4
          }}>
            {currentCampaigns.map((campaign) => (
              <CampaignCard 
                key={campaign.id} 
                campaign={campaign} 
                onClick={() => handleCampaignClick(campaign.id)}
              />
            ))}
          </Box>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            검색 결과가 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary">
            다른 검색어나 필터를 사용해보세요.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CampaignListPage; 